import { Component, Inject, ViewChild, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { DashpivotEvent, EventTypes } from '@site-mate/dashpivot-shared-library';

import { appControlType } from 'app/apps/app-control-type.model';
// eslint-disable-next-line import/no-cycle
import { FormRegisterViewComponent } from 'app/apps/template-forms/template-forms-register/form-register-view.component';
import { RegisterViewSettingComponent } from 'app/apps/template-forms/template-forms-register/settings/register-view-setting.component';
import { FormService } from 'app/form/form.service';
import { SegmentService } from 'app/segment/segment.service';
import { WINDOW } from 'app/shared/factory/window.factory';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { DataCompressionService } from 'app/shared/service/data-compression/data-compression.service';
import { DownloaderService } from 'app/shared/service/downloader.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { LocalStorageService } from 'app/shared/service/local-storage.service';
import { RouteParamsService } from 'app/shared/service/route-params.service';
import { TimingMarkerService } from 'app/shared/service/timing-marker.service';
import { ToastrService } from 'app/shared/service/toastr.service';

import { TemplateFormsBaseComponent } from '../template-forms-base.component';

@Component({
  selector: 'cc-template-forms-register',
  templateUrl: './template-forms-register.component.html',
  styleUrls: ['./template-forms-register.component.scss'],
})
export class TemplateFormsRegisterComponent extends TemplateFormsBaseComponent implements OnInit {
  @ViewChild(FormRegisterViewComponent)
  formRegisterView: FormRegisterViewComponent;

  readonly selectedForms = new Set<string>();

  controlType = appControlType;
  loading = false;
  templateId;
  formId;
  app;
  isRegisterView;
  selectAll = { selected: false };
  downloadingPDF;
  exportingCSV;
  exportingPDF;
  registerViewFilterCount = { value: 0 };
  canAdd = true;

  constructor(
    private readonly confirmService: ConfirmService,
    private readonly formService: FormService,
    private readonly modal: NgbModal,
    private readonly toastr: ToastrService,
    @Inject(WINDOW) private readonly window: Window,
    protected readonly errorHandler: ErrorHandler,
    protected readonly route: ActivatedRoute,
    protected readonly downloaderService: DownloaderService,
    protected readonly router: Router,
    protected readonly routeParamsService: RouteParamsService,
    protected readonly timingMarkerService: TimingMarkerService,
    protected readonly segmentService: SegmentService,
    private readonly localStorageService: LocalStorageService,
    private readonly dataCompressionService: DataCompressionService,
  ) {
    super(route, router, errorHandler, routeParamsService, timingMarkerService, segmentService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.trackViewedEvent('Register');
  }

  onSetRegisterView() {
    const ref = this.modal.open(RegisterViewSettingComponent);
    ref.componentInstance.template = this.app;
  }

  resetRegisterViewFilter() {
    this.formService.resetRegisterFilter();
  }

  exportCsvVisibleColumns() {
    const url = this.downloaderService.getExportCSVUrl({
      templateId: this.templateId,
      hierarchy: this.getHierarchy(this.app).toLowerCase(),
      queryParams: this.getFormsRequestParams(),
    });
    this.window.open(`${url}`, '_blank');
  }

  exportCsvAllColumns() {
    const queryParams = { ...this.getFormsRequestParams(), includeAllColumns: true };
    const url = this.downloaderService.getExportCSVUrl({
      templateId: this.templateId,
      hierarchy: this.getHierarchy(this.app).toLowerCase(),
      queryParams,
    });
    this.window.open(`${url}`, '_blank');
  }

  clearSelection() {
    this.selectedForms.clear();
  }

  exportPDF() {
    const url = this.downloaderService.getExportFormsUrl({
      templateId: this.templateId,
      hierarchy: this.getHierarchy(this.app).toLowerCase(),
      queryParams: this.getFormsRequestParams(),
    });

    this.window.open(`${url}`, '_blank');
  }

  downloadForms() {
    const url = this.downloaderService.getDownloadFormsUrl({
      templateId: this.templateId,
      hierarchy: this.getHierarchy(this.app).toLowerCase(),
      queryParams: this.getFormsRequestParams(),
    });

    this.window.open(`${url}`, '_blank');
  }

  exportsRegisterOnly() {
    const url = this.downloaderService.getRegisterOnlyUrl({
      templateId: this.templateId,
      hierarchy: this.getHierarchy(this.app).toLowerCase(),
      queryParams: this.getFormsRequestParams(),
    });

    this.window.open(`${url}`, '_blank');
  }

  exportPDFWithOriginalPhotos() {
    const queryParams = { ...this.getFormsRequestParams(), includeOriginalImages: true };
    const url = this.downloaderService.getDownloadFormsUrl({
      templateId: this.templateId,
      hierarchy: this.getHierarchy(this.app).toLowerCase(),
      queryParams,
    });

    this.window.open(`${url}`, '_blank');
  }

  getCsvFormsRequestParams() {
    const params = { ...this.route.snapshot.queryParams };
    if (!this.selectAll.selected) {
      params.id = JSON.stringify([...this.selectedForms]);
    }
    return params;
  }

  async onDeleteSelected() {
    let timingMarker;
    try {
      const selectedIds = [...this.selectedForms];
      await this.confirmService.confirmDelete('deleteSelectedForms');
      timingMarker = this.timingMarkerService.start(
        new DashpivotEvent(EventTypes.FormsRegisterActionApplied, {
          Context: 'Delete Selected',
          Hierarchy: this.getHierarchy(this.app),
          templateId: this.templateId,
          formIds: selectedIds,
        }),
      );
      this.formRegisterView.setLoadingState(true);
      const formRemovalPromises = selectedIds.map((formId) => this.formService.remove(formId).toPromise());
      await Promise.all(formRemovalPromises);
      this.timingMarkerService.finish(timingMarker);
      this.toastr.successByKey('deletedSelectedForms');
      this.formRegisterView.removeSelectedForms();
      this.clearSelection();
    } catch (error) {
      this.timingMarkerService.finishWithFailure(timingMarker, error);
      if (typeof error !== 'string') {
        this.toastr.error('deleteSelectedFormsError');
      }
    } finally {
      this.formRegisterView.setLoadingState(false);
    }
  }

  private getFormsRequestParams() {
    const params = { ...this.route.snapshot.queryParams };

    if (!this.selectAll.selected) {
      const storageKey = this.downloaderService.getRandomDownloadId();
      this.setCompressedFormsInLocalStorageItem(storageKey);
      params.downloadId = storageKey;
    }

    return params;
  }

  private getHierarchy(template) {
    return template.isOrganisationTemplate ? 'Organisation' : 'Team';
  }

  private setCompressedFormsInLocalStorageItem(key: string) {
    this.localStorageService.setItem(
      key,
      JSON.stringify({
        ids: this.dataCompressionService.compress(JSON.stringify([...this.selectedForms])),
        timestamp: Date.now(),
        type: this.downloaderService.downloaderType,
      }),
    );
  }
}
